import {
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as styles from './navigation-rail-collapse.css';

interface NavigationRailCollapseProps extends Omit<
  HTMLAttributes<HTMLElement>,
  'children'
> {
  as?: 'div' | 'nav';
  children: ReactNode;
  contentRef?: Ref<HTMLDivElement>;
  expanded: boolean;
}

/** One motion and accessibility contract for every nested static-rail level. */
export function NavigationRailCollapse({
  as: Component = 'div',
  children,
  contentRef,
  expanded,
  style,
  ...props
}: NavigationRailCollapseProps) {
  const measuredContentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const assignContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      measuredContentRef.current = node;
      if (typeof contentRef === 'function') {
        contentRef(node);
      } else if (contentRef) {
        contentRef.current = node;
      }
    },
    [contentRef]
  );

  useEffect(() => {
    const content = measuredContentRef.current;
    if (!content) return;

    const measure = () => {
      const nextHeight = Math.ceil(content.getBoundingClientRect().height);
      // Keep the last real height during a portal unmount. It provides the
      // starting point for the Workspace rail's closing transition.
      if (nextHeight > 0) setContentHeight(nextHeight);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    measure();
    return () => observer.disconnect();
  }, []);

  const collapseStyle: CSSProperties = {
    ...style,
    height: expanded ? contentHeight : 0,
  };

  return (
    <Component
      {...props}
      aria-hidden={!expanded}
      className={styles.root}
      data-expanded={expanded}
      inert={expanded ? undefined : true}
      style={collapseStyle}
    >
      <div className={styles.clip}>
        <div className={styles.content} ref={assignContentRef}>
          {children}
        </div>
      </div>
    </Component>
  );
}
